/**
 * GrantReadiness — `/grant-readiness`.
 *
 * Visual parity target: `/readiness` (Readiness.jsx). Same Shell layout,
 * same hero-with-tier-label styling, same weaknesses / next-steps
 * sections. NO per-grant eligibility badges here — score and badges
 * stay visually separate (Frontend Discipline 2 from the G1 dispatch).
 *
 * The Opportunity Readiness Dashboard planned for a later phase will
 * unify Credit + Grant readiness under one shell — mirroring this
 * page's layout is the pre-work.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCcw, Target, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import GrantShell from "@/components/grants/GrantShell";
import { PageHeader } from "@/design-system/components/PageHeader";
import { Card } from "@/design-system/components/Card";
import { toast } from "sonner";

const TIER_COLOR = {
  grant_ready: { color: "var(--ds-color-success)", tint: "var(--ds-color-success-soft)" },
  almost_grant_ready: { color: "var(--ds-color-info)", tint: "var(--ds-color-info-soft)" },
  grant_improvement_recommended: { color: "var(--ds-color-warning)", tint: "var(--ds-color-warning-soft)" },
  not_grant_ready: { color: "var(--ds-color-danger)", tint: "var(--ds-color-danger-soft)" },
};

const CATEGORY_LABEL = {
  business_foundation: "Business Foundation",
  financial_readiness: "Financial Readiness",
  eligibility_compliance: "Eligibility & Compliance",
  documentation_completeness: "Documentation Completeness",
  organizational_capacity: "Organizational Capacity",
  project_readiness: "Project Readiness",
  community_alignment: "Community / Strategic Alignment",
};

function SectionLabel({ children, testId }) {
  return (
    <p
      data-testid={testId}
      className="text-xs uppercase tracking-[0.28em] text-[var(--ds-color-ink-muted)]"
    >
      {children}
    </p>
  );
}

export default function GrantReadiness() {
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.get("/grant-readiness/latest");
        if (!cancelled) setAssessment(res.data);
      } catch (err) {
        // 404 = no assessment yet — that's fine, the CTA below asks the user to compute one.
        if (err?.response?.status !== 404) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  async function recalc() {
    setRecalculating(true);
    try {
      const res = await api.post("/grant-readiness/calculate");
      setAssessment(res.data);
      toast.success("Grant readiness updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRecalculating(false);
    }
  }

  if (loading) {
    return (
      <GrantShell testId="grant-readiness-root">
        <div className="mt-10 text-sm text-[var(--ds-color-ink-muted)]">Loading grant readiness…</div>
      </GrantShell>
    );
  }

  if (!assessment) {
    return (
      <GrantShell testId="grant-readiness-root">
        <PageHeader
          eyebrow="Grant Navigator"
          title="Grant Readiness Score"
          data-testid="grant-readiness-header"
        />
        <div
          className="mt-2 rounded-[var(--ds-radius-card)] p-10 text-center"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px dashed var(--ds-color-border-strong)",
          }}
        >
          <Target
            className="mx-auto w-5 h-5"
            style={{ color: "var(--ds-color-accent)" }}
            aria-hidden="true"
          />
          <p
            className="mt-3 text-[14px] font-semibold"
            style={{ color: "var(--ds-color-ink)" }}
          >
            No grant readiness score yet.
          </p>
          <p
            className="mt-2 text-[13px]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Complete your grant profile and calculate your first score to see
            where you stand across the seven readiness categories.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/grant-profile"
              data-testid="grant-readiness-goto-profile"
              className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-colors"
              style={{
                border: "1px solid var(--ds-color-border-strong)",
                color: "var(--ds-color-ink)",
                background: "var(--ds-color-surface)",
                transitionDuration: "var(--ds-duration-fast)",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-surface-muted)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-surface)")
              }
            >
              Update Grant Profile
            </Link>
            <button
              type="button"
              onClick={recalc}
              disabled={recalculating}
              data-testid="grant-readiness-calc-first"
              className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-colors disabled:opacity-60"
              style={{
                background: "var(--ds-color-accent)",
                color: "var(--ds-color-accent-ink)",
                transitionDuration: "var(--ds-duration-fast)",
              }}
              onMouseEnter={(e) =>
                !recalculating &&
                (e.currentTarget.style.background = "var(--ds-color-accent-strong)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-accent)")
              }
            >
              <RefreshCcw className="w-4 h-4" /> Calculate my score
            </button>
          </div>
        </div>
      </GrantShell>
    );
  }

  const tierMeta = TIER_COLOR[assessment.tier] || TIER_COLOR.not_grant_ready;

  return (
    <GrantShell testId="grant-readiness-root">
      <PageHeader
        eyebrow="Grant Navigator"
        title="Grant Readiness Score"
        data-testid="grant-readiness-header"
      />

      {/* HERO — parity with /readiness, tightened. */}
      <Card
        data-testid="grant-readiness-hero"
        className="mt-2 rounded-[var(--ds-radius-card)]"
      >
        <div className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div>
              <SectionLabel testId="grant-readiness-score-label">
                Your grant readiness score
              </SectionLabel>
              <p
                data-testid="grant-readiness-score-value"
                className="mt-3 text-[56px] leading-none font-semibold tracking-tight"
                style={{
                  color: "var(--ds-color-ink)",
                  fontFamily: "var(--ds-font-display)",
                }}
              >
                {assessment.total_score}
                <span
                  className="ml-1 text-[16px]"
                  style={{ color: "var(--ds-color-ink-muted)" }}
                >
                  / 100
                </span>
              </p>
              <span
                data-testid="grant-readiness-tier-label"
                className="mt-4 inline-flex items-center gap-2 rounded-[var(--ds-radius-pill)] px-3 py-1 text-[11px] uppercase tracking-[0.16em] font-semibold"
                style={{
                  background: tierMeta.tint,
                  border: `1px solid ${tierMeta.color}55`,
                  color: tierMeta.color,
                }}
              >
                {assessment.tier_label}
              </span>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3">
              <button
                type="button"
                onClick={recalc}
                disabled={recalculating}
                data-testid="grant-readiness-recalc-btn"
                className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-colors disabled:opacity-60"
                style={{
                  border: "1px solid var(--ds-color-border-strong)",
                  color: "var(--ds-color-ink)",
                  background: "var(--ds-color-surface)",
                  transitionDuration: "var(--ds-duration-fast)",
                }}
              >
                <RefreshCcw className="w-4 h-4" /> {recalculating ? "Recomputing…" : "Recompute"}
              </button>
              <Link
                to="/grant-profile"
                data-testid="grant-readiness-update-profile"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] hover:underline"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                Update grant profile <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </Card>

      {/* CATEGORY BREAKDOWN. */}
      <section className="mt-6">
        <SectionLabel>Category breakdown</SectionLabel>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(assessment.category_scores || {}).map(([key, score]) => (
            <Card
              key={key}
              data-testid={`grant-readiness-category-${key}`}
              className="rounded-[var(--ds-radius-card)]"
            >
              <div className="p-4">
                <p
                  className="text-[11px] uppercase tracking-[0.16em] font-semibold"
                  style={{ color: "var(--ds-color-ink-subtle)" }}
                >
                  {CATEGORY_LABEL[key] || key}
                </p>
                <p
                  className="mt-2 text-[22px] font-semibold"
                  style={{ color: "var(--ds-color-ink)" }}
                >
                  {score}
                  <span
                    className="text-[11px]"
                    style={{ color: "var(--ds-color-ink-muted)" }}
                  >
                    {" "}
                    / 100
                  </span>
                </p>
                <div
                  className="mt-3 h-1.5 w-full rounded-[var(--ds-radius-pill)]"
                  style={{ background: "var(--ds-color-surface-inset)" }}
                >
                  <div
                    className="h-full rounded-[var(--ds-radius-pill)]"
                    style={{
                      width: `${Math.min(score, 100)}%`,
                      background: "var(--ds-color-accent)",
                    }}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* STRENGTHS / WEAKNESSES parity block. */}
      <section className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card
          data-testid="grant-readiness-strengths"
          className="rounded-[var(--ds-radius-card)]"
        >
          <div className="p-5">
            <SectionLabel>Strengths</SectionLabel>
            {(assessment.strengths || []).length === 0 ? (
              <p
                className="mt-3 text-[13px]"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                Nothing showing as a strength yet. Complete more of your grant
                profile and rerun the score.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {assessment.strengths.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-2 text-[13px]"
                    style={{ color: "var(--ds-color-ink)" }}
                  >
                    <CheckCircle2
                      className="mt-0.5 w-4 h-4 flex-shrink-0"
                      style={{ color: "var(--ds-color-success)" }}
                    />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card
          data-testid="grant-readiness-weaknesses"
          className="rounded-[var(--ds-radius-card)]"
        >
          <div className="p-5">
            <SectionLabel>What to improve next</SectionLabel>
            {(assessment.weaknesses || []).length === 0 ? (
              <p
                className="mt-3 text-[13px]"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                No gaps flagged. Recompute after your next profile update to
                confirm.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {assessment.weaknesses.map((w) => (
                  <li
                    key={w}
                    className="flex items-start gap-2 text-[13px]"
                    style={{ color: "var(--ds-color-ink)" }}
                  >
                    <AlertTriangle
                      className="mt-0.5 w-4 h-4 flex-shrink-0"
                      style={{ color: "var(--ds-color-warning)" }}
                    />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </section>

      {/* CHECKLIST parity block. */}
      <section className="mt-6">
        <SectionLabel>Readiness checklist</SectionLabel>
        <Card
          data-testid="grant-readiness-checklist"
          className="mt-3 rounded-[var(--ds-radius-card)]"
        >
          <div className="p-5">
            <ul
              className="divide-y"
              style={{ borderColor: "var(--ds-color-border)" }}
            >
              {(assessment.checklist || []).map((c) => {
                const done = c.status === "complete";
                return (
                  <li
                    key={c.item}
                    data-testid={`grant-readiness-checklist-item-${c.status}`}
                    className="flex items-start gap-3 py-3"
                  >
                    <CheckCircle2
                      className="mt-0.5 w-4 h-4 flex-shrink-0"
                      style={{
                        color: done
                          ? "var(--ds-color-success)"
                          : "var(--ds-color-border-strong)",
                      }}
                    />
                    <div>
                      <p
                        className="text-[13px]"
                        style={{
                          color: done
                            ? "var(--ds-color-ink)"
                            : "var(--ds-color-ink-muted)",
                        }}
                      >
                        {c.item}
                      </p>
                      {c.category && (
                        <p
                          className="mt-0.5 text-[10px] uppercase tracking-[0.14em]"
                          style={{ color: "var(--ds-color-ink-muted)" }}
                        >
                          {CATEGORY_LABEL[c.category] || c.category}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </Card>
      </section>
    </GrantShell>
  );
}
